import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { TrendingUp, Users, BarChart2, LineChart, Users2, Car, Building2, CalendarClock } from 'lucide-react'
import { useMaxDataDate } from '../hooks/useMaxDataDate'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import PostnummerFilter, { type PostnummerDirection } from '../components/PostnummerFilter'
import BruktbilfinansieringFilterBar, { type DatePreset } from './bruktbilfinansiering/BruktbilfinansieringFilterBar'
import OverviewTab from './bruktbilfinansiering/OverviewTab'
import DealersTab from './bruktbilfinansiering/DealersTab'
import HendelserTab from './bruktbilfinansiering/HendelserTab'
// import DatakvalitetTab from './bruktbilfinansiering/DatakvalitetTab'
import KlasserTab from './bruktbilfinansiering/KlasserTab'
import LanebelopProfilTab from './bruktbilfinansiering/LanebelopProfilTab'
import AlderProfilTab from './bruktbilfinansiering/AlderProfilTab'
import BilalderTab from './bruktbilfinansiering/BilalderTab'
import BankTab from './bruktbilfinansiering/BankTab'
import { useDatakvalitetFreshness } from './bruktbilfinansiering/hooks/useDatakvalitet'
import { useBanks } from './bruktbilfinansiering/hooks/useBanks'
import type { BruktbilfinansieringFilters } from './bruktbilfinansiering/hooks/useBruktbilfinansiering'
import { ALL_SCOPE1_CLASSES } from '../types/bruktbilfinansiering'

const STORAGE_KEY = 'bruktbilfinans:lastBankOrgNr'

const LAG_DAYS = 15

function toLocalISO(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function subtractDays(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return toLocalISO(d)
}

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

type ActiveTab = 'oversikt' | 'forhandlere' | 'hendelser' | 'klasser' | 'bilalder' | 'datakvalitet' | 'lanebelop-profil' | 'alder-profil' | 'bankmarked'

export type BuyerType = 'PERSON' | 'FIRMA' | 'BILUTLEIE'
export const ALL_BUYER_TYPES: BuyerType[] = ['PERSON', 'FIRMA', 'BILUTLEIE']

function computePresetDates(preset: 'forrige_maned' | 'hittil_i_ar' | 'annen') {
  const today = new Date()
  if (preset === 'forrige_maned') {
    const lastDayPrevMonth = new Date(today.getFullYear(), today.getMonth(), 0)
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - LAG_DAYS)
    const end = lastDayPrevMonth < cutoff ? lastDayPrevMonth : cutoff
    return {
      startDate: toLocalISO(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      endDate: toLocalISO(end),
    }
  }
  if (preset === 'hittil_i_ar') {
    return {
      startDate: `${today.getFullYear()}-01-01`,
      endDate: subtractDays(LAG_DAYS),
    }
  }
  return null
}

export default function BruktbilfinansieringPage() {
  const { t } = useTranslation('bbf')
  const { bankOrgNr = '911629283' } = useParams<{ bankOrgNr: string }>()
  const maxDate = useMaxDataDate()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { banks, loading: banksLoading } = useBanks()
  const currentBank = banks.find(b => b.bank_org_nr === bankOrgNr)
  const bankName = currentBank?.bank_name ?? bankOrgNr
  const freshness = useDatakvalitetFreshness(bankOrgNr)
  const dataCutoffDate = subtractDays(LAG_DAYS)

  useEffect(() => {
    if (!banksLoading && banks.length > 0 && !currentBank) {
      const storedBank = localStorage.getItem(STORAGE_KEY)
      const target = banks.find(b => b.bank_org_nr === storedBank) ?? banks[0]
      navigate(`/bruktbil-finansiering/${target.bank_org_nr}`, { replace: true })
    }
  }, [banksLoading, banks, currentBank, navigate])

  useEffect(() => {
    if (currentBank) {
      localStorage.setItem(STORAGE_KEY, bankOrgNr)
    }
    // currentBank is derived from bankOrgNr and banks; only re-run when bankOrgNr changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankOrgNr])

  const { getVarebilParam, setVehicleFilter } = useVehicleFilter()

  // Default to 'alle' on this page (global default is 'personbil').
  // The effect only lands after the first render, so until it does we must not
  // read the app-wide default — every tab keys its fetch on filters.varebil, so
  // rendering once with 'personbil' fired a request with ?varebil=false and then
  // immediately refetched without it: one wasted request per tab, and a visible
  // flash of personbil-only numbers on every page load.
  const [vehicleDefaultApplied, setVehicleDefaultApplied] = useState(false)
  useEffect(() => {
    setVehicleFilter('alle')
    setVehicleDefaultApplied(true)
  }, [setVehicleFilter])
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = useState(false)
  const [postnummerDirection, setPostnummerDirection] = useState<PostnummerDirection>('bilkjøper')

  const initialTab = (searchParams.get('tab') as ActiveTab) || 'oversikt'
  const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab)
  const INITIAL_PRESET = 'hittil_i_ar' as const
  const initialDates = computePresetDates(INITIAL_PRESET)!
  const [sortBy, setSortBy] = useState('total_in_scope_sales')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [draftPreset, setDraftPreset] = useState<DatePreset | null>(INITIAL_PRESET)

  // Buyer-type filter (Forhandlere tab only)
  const [selectedBuyerTypes, setSelectedBuyerTypes] = useState<BuyerType[]>([...ALL_BUYER_TYPES])

  function toggleBuyerType(bt: BuyerType) {
    setSelectedBuyerTypes(prev =>
      prev.includes(bt) ? prev.filter(x => x !== bt) : [...prev, bt]
    )
  }

  function resetBuyerTypes() {
    setSelectedBuyerTypes([...ALL_BUYER_TYPES])
  }

  // Draft state (filter UI)
  const [draftStartDate, setDraftStartDate] = useState(initialDates.startDate)
  const [draftEndDate, setDraftEndDate] = useState(initialDates.endDate)
  const [draftClasses, setDraftClasses] = useState<string[]>(() => {
    const fromUrl = searchParams.getAll('classes')
    return fromUrl.length ? fromUrl : [...ALL_SCOPE1_CLASSES]
  })

  // Applied state (what tabs use)
  const [appliedStartDate, setAppliedStartDate] = useState(initialDates.startDate)
  const [appliedEndDate, setAppliedEndDate] = useState(initialDates.endDate)
  const [appliedClasses, setAppliedClasses] = useState<string[]>(() => {
    const fromUrl = searchParams.getAll('classes')
    return fromUrl.length ? fromUrl : [...ALL_SCOPE1_CLASSES]
  })
  const [appliedPostnummerDirection, setAppliedPostnummerDirection] = useState<PostnummerDirection>('bilkjøper')
  // Snapshot of the resolved postnummer string, captured at apply-time.
  // Changing markedsområde only updates the draft (live) hook — the applied
  // value stays frozen until the user clicks Oppdater.
  const [appliedPostnummer, setAppliedPostnummer] = useState<string | null>(null)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)

  function applyFilters() {
    setAppliedStartDate(draftStartDate)
    setAppliedEndDate(draftEndDate)
    setAppliedClasses([...draftClasses])
    setAppliedPostnummer(postnummerFilterEnabled ? minePostnummer : null)
    setAppliedPostnummerDirection(postnummerDirection)
  }

  const isDirty = draftStartDate !== appliedStartDate ||
    draftEndDate !== appliedEndDate ||
    JSON.stringify([...draftClasses].sort()) !== JSON.stringify([...appliedClasses].sort()) ||
    (postnummerFilterEnabled ? minePostnummer : null) !== appliedPostnummer ||
    postnummerDirection !== appliedPostnummerDirection

  // Classes are now always all scope=1 — no URL sync needed

  function toggleClass(c: string) {
    setDraftClasses(prev =>
      prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]
    )
  }

  function applyPreset(preset: DatePreset) {
    const dates = computePresetDates(preset)
    if (dates) {
      // Presets apply immediately — update both draft and applied
      setDraftStartDate(dates.startDate)
      setDraftEndDate(dates.endDate)
      setAppliedStartDate(dates.startDate)
      setAppliedEndDate(dates.endDate)
    }
    setDraftPreset(preset)
  }

  const filters: BruktbilfinansieringFilters = {
    startDate: appliedStartDate,
    endDate: appliedEndDate,
    buyerPostnummer: (appliedPostnummerDirection === 'bilkjøper' || appliedPostnummerDirection === 'begge') ? appliedPostnummer : null,
    dealerPostnummer: (appliedPostnummerDirection === 'bilselger' || appliedPostnummerDirection === 'begge') ? appliedPostnummer : null,
    varebil: vehicleDefaultApplied ? getVarebilParam() : undefined,
    classes: appliedClasses,
    postnummerDirection: appliedPostnummerDirection,
  }

  // Build Forhandlere-specific filters: use appliedClasses (same as Oversikt),
  // further filtered by buyer-type checkboxes when a subset is selected.
  const forhandlereClasses = selectedBuyerTypes.length === ALL_BUYER_TYPES.length
    ? appliedClasses
    : appliedClasses.filter((c: string) =>
        selectedBuyerTypes.some(bt => c.endsWith(`_TO_${bt}`))
      )
  const forhandlereFilters: BruktbilfinansieringFilters = { ...filters, classes: forhandlereClasses }

  const handleSortChange = (col: string, dir: 'asc' | 'desc') => {
    setSortBy(col)
    setSortDir(dir)
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('page.title')}</h1>
          {freshness?.latest_transfer_event_at && (
            <span
              data-testid="freshness-pill"
              className="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded-full px-3 py-1"
            >
              {t('page.datasetUpdated', { date: new Date(freshness.latest_transfer_event_at).toLocaleDateString('nb-NO') })}
              {freshness.latest_brreg_check_at && (
                <> &middot; Brreg: {new Date(freshness.latest_brreg_check_at).toLocaleDateString('nb-NO')}</>
              )}
            </span>
          )}
          <span
            className="text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 rounded-full px-3 py-1"
            title={t('page.cutoffTooltip', { date: fmtDate(dataCutoffDate), days: LAG_DAYS })}
          >
            {t('page.dataComplete', { date: fmtDate(dataCutoffDate) })}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">{t('page.bank')}</span>
          {banks.length > 1 ? (
            <select
              value={bankOrgNr}
              onChange={e => navigate(`/bruktbil-finansiering/${e.target.value}`)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-sm px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-medium"
            >
              {banks.map(b => (
                <option key={b.bank_org_nr} value={b.bank_org_nr}>{b.bank_name}</option>
              ))}
            </select>
          ) : (
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{bankName}</span>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <BruktbilfinansieringFilterBar
        startDate={draftStartDate}
        endDate={draftEndDate}
        onStartDateChange={setDraftStartDate}
        onEndDateChange={setDraftEndDate}
        maxDate={maxDate}
        preset={draftPreset}
        onPresetClick={applyPreset}
        allClasses={[]}
        selectedClasses={[]}
        onToggleClass={toggleClass}
        showClassFilter={false}
        showBuyerTypeFilter={activeTab === 'forhandlere'}
        selectedBuyerTypes={selectedBuyerTypes}
        onToggleBuyerType={toggleBuyerType}
        onResetBuyerTypes={resetBuyerTypes}
        onApply={applyFilters}
        isDirty={isDirty}
      />

      {/* Mine postnummer toggle */}
      <PostnummerFilter
        compact
        enabled={postnummerFilterEnabled}
        onToggle={setPostnummerFilterEnabled}
        direction={postnummerDirection}
        onDirectionChange={setPostnummerDirection}
      />

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 gap-1">
        <button
          onClick={() => setActiveTab('oversikt')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'oversikt'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            {t('page.tabs.oversikt')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('forhandlere')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'forhandlere'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            {t('page.tabs.forhandlere')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('hendelser')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'hendelser'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4" />
            {t('page.tabs.hendelser')}
          </span>
        </button>
        {/* Datakvalitet tab hidden — too technical for bank users
        <button
          onClick={() => setActiveTab('datakvalitet')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'datakvalitet'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Datakvalitet
          </span>
        </button>
        */}
        <button
          onClick={() => setActiveTab('bankmarked')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'bankmarked'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {t('page.tabs.bankmarked')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('klasser')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'klasser'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <BarChart2 className="w-4 h-4" />
            {t('page.tabs.klasser')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('lanebelop-profil')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'lanebelop-profil'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <LineChart className="w-4 h-4" />
            {t('page.tabs.lanebelopProfil')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('alder-profil')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'alder-profil'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Users2 className="w-4 h-4" />
            {t('page.tabs.alderProfil')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('bilalder')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'bilalder'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Car className="w-4 h-4" />
            {t('page.tabs.bilalder')}
          </span>
        </button>
      </div>

      {/* Tab panels */}
      {activeTab === 'oversikt' && (
        <OverviewTab bankOrgNr={bankOrgNr} bankName={bankName} filters={filters} />
      )}
      {activeTab === 'forhandlere' && (
        <DealersTab
          bankOrgNr={bankOrgNr}
          bankName={bankName}
          filters={forhandlereFilters}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={handleSortChange}
        />
      )}
      {activeTab === 'hendelser' && (
        <HendelserTab bankOrgNr={bankOrgNr} bankName={bankName} />
      )}
      {/* Datakvalitet tab hidden
      {activeTab === 'datakvalitet' && (
        <DatakvalitetTab bankOrgNr={bankOrgNr} bankName={bankName} filters={filters} />
      )}
      */}
      {activeTab === 'bankmarked' && (
        <BankTab bankOrgNr={bankOrgNr} bankName={bankName} filters={filters} />
      )}
      {activeTab === 'klasser' && (
        <KlasserTab bankOrgNr={bankOrgNr} bankName={bankName} filters={filters} />
      )}
      {activeTab === 'lanebelop-profil' && (
        <LanebelopProfilTab bankOrgNr={bankOrgNr} bankName={bankName} filters={filters} />
      )}
      {activeTab === 'alder-profil' && (
        <AlderProfilTab bankOrgNr={bankOrgNr} bankName={bankName} filters={filters} />
      )}
      {activeTab === 'bilalder' && (
        <BilalderTab bankOrgNr={bankOrgNr} bankName={bankName} filters={filters} />
      )}
    </div>
  )
}
